import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { IWeissmanModalComponent } from '../../Compliance/WeissmanModalService';
import { RevisionChangeTypes } from '../../constants.new';
import { ActionViewPersistenceService } from '../../Task/ActionView/Action.View.Persistence.Service.upgrade';
import { ProcessingService } from '../processing.service.upgrade';

export interface ProcessNoAppealParams {
    taskIDs: number[];
    searchTimestamp: Date;
    updateAppealRecommendation: boolean;
}

export interface ProcessNoAppealForm {
    taskIDs: number[];
    revisionChangeTypeID: number;
    newRevisionDescription: string;
    comment: string;
    isPinned: boolean;
    searchTimestamp: Date;
    updateAppealRecommendation: boolean;
}

@Component({
    selector: 'process-no-appeal',
    templateUrl: './processNoAppeal.component.html',
    standalone: false,
})
export class ProcessNoAppealComponent
    implements OnInit, IWeissmanModalComponent<ProcessNoAppealParams, Core.BulkOperationResult[]>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _actionViewPersistenceService: ActionViewPersistenceService,
        private readonly _processingService: ProcessingService,
    ) {}

    @ViewChild('other') other: ElementRef;

    params: ProcessNoAppealParams;
    result: Core.BulkOperationResult[];

    loading: boolean;
    loadingMessage: string;
    payload: ProcessNoAppealForm;
    revisionChangeTypes = RevisionChangeTypes;
    isClientApprovalRequired: boolean;

    async ngOnInit(): Promise<void> {
        this.loading = true;

        try {
            const result = await this._processingService.getAppealLevels(this.params.taskIDs);
            this.isClientApprovalRequired = result.isClientApprovalRequired;

            const remembered = this.getRememberedRevisionChange();
            this.payload = {
                taskIDs: this.params.taskIDs,
                revisionChangeTypeID: remembered.revisionChangeTypeID,
                newRevisionDescription: remembered.newRevisionDescription,
                comment: null,
                isPinned: false,
                searchTimestamp: this.params.searchTimestamp,
                updateAppealRecommendation: this.params.updateAppealRecommendation,
            };
        } finally {
            this.loading = false;
        }
    }

    focus(): void {
        if (this.other) {
            this.other.nativeElement.focus();
        }
    }

    get hasCommentText(): boolean {
        return !!this.payload?.comment?.trim();
    }

    onCommentChange(): void {
        if (!this.hasCommentText) {
            this.payload.isPinned = false;
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    async process(): Promise<void> {
        this.loadingMessage = 'Processing...';
        this.loading = true;
        this._actionViewPersistenceService.noAppealRevisionChangeTypeID = this.payload.revisionChangeTypeID;
        this._actionViewPersistenceService.noAppealRevisionDescription = this.payload.newRevisionDescription;

        try {
            const bufferSize = 20;
            for (let i = 0; i < this.payload.taskIDs.length; i += bufferSize) {
                const chunk = structuredClone(this.payload);
                chunk.taskIDs = this.payload.taskIDs.slice(i, i + bufferSize);

                this.loadingMessage = `Processing Tasks ${i + 1} - ${i + chunk.taskIDs.length} of ${this.payload.taskIDs.length}...`;

                this.result = await this._processingService.processNoAppealWarranted(chunk);
            }
            this._bsModalRef.hide();
        } finally {
            this.loading = false;
            this.loadingMessage = null;
        }
    }

    private getRememberedRevisionChange(): { revisionChangeTypeID: number; newRevisionDescription: string } {
        const typeID = this._actionViewPersistenceService.noAppealRevisionChangeTypeID;
        const description = this._actionViewPersistenceService.noAppealRevisionDescription;

        if (typeID === RevisionChangeTypes.OTHER) {
            if (description?.trim()) {
                return { revisionChangeTypeID: typeID, newRevisionDescription: description };
            }
        } else if (
            typeID === RevisionChangeTypes.ORIGINAL_TO_FINAL ||
            typeID === RevisionChangeTypes.FINAL ||
            typeID === RevisionChangeTypes.NO_CHANGE
        ) {
            return { revisionChangeTypeID: typeID, newRevisionDescription: '' };
        }

        return { revisionChangeTypeID: RevisionChangeTypes.ORIGINAL_TO_FINAL, newRevisionDescription: '' };
    }
}
